import type { FC, PropsWithChildren } from 'react';
import { useRef } from 'react';

import type { Attributes, HeadElement } from '@xing-com/crate-xinglet/internal';
import { useRuntime } from '@xing-com/crate-xinglet/internal';

import type { HelmetContextType } from './context';
import { HelmetContext } from './context';
import type { HelmetState } from './merge-state';
import { mergeState } from './merge-state';

function hasStyleSheetAttr(
  element: Element
): element is Element & { styleSheet: CSSRule } {
  return 'styleSheet' in element;
}

function updateAttributes(
  element: Element,
  oldAttributes: Attributes,
  newAttributes: Attributes
): void {
  for (const [key, value] of Object.entries(newAttributes)) {
    element.setAttribute(key, value || '');
  }

  for (const key of Object.keys(oldAttributes)) {
    if (key in newAttributes) continue;
    element.removeAttribute(key);
  }
}

function findOrCreateDomElement({
  kind,
  attributes,
  content = '',
}: HeadElement): [boolean, Element] {
  const element = document.createElement(kind);

  if (kind === 'script') {
    element.innerHTML = content;
  } else if (kind === 'style') {
    if (hasStyleSheetAttr(element)) {
      element.styleSheet.cssText = content;
    } else {
      element.appendChild(document.createTextNode(content));
    }
  }

  element.setAttribute('data-ch', '');

  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value ?? '');
  }

  const query = `${kind}[data-ch]`;
  const elements = Array.from(document.querySelectorAll(query));
  const isExisting = elements.find((node) => node.isEqualNode(element));

  return isExisting ? [true, isExisting] : [false, element];
}

function updateDom(oldState: HelmetState, newState: HelmetState): void {
  const {
    defaultTitle,
    title,
    titleTemplate,
    htmlAttributes,
    bodyAttributes,
    elements,
  } = newState;

  const fullTitle =
    (title && titleTemplate?.replace(/%s/g, title)) ??
    title ??
    defaultTitle ??
    '';
  if (LOCAL_DEVELOPMENT) console.debug('[CrateHelmet] title set', fullTitle);
  document.title = fullTitle;

  const html = document.getElementsByTagName('html')[0];
  if (html) {
    updateAttributes(html, oldState.htmlAttributes, htmlAttributes);
  }
  const body = document.getElementsByTagName('body')[0];
  if (body) {
    updateAttributes(body, oldState.bodyAttributes, bodyAttributes);
  }

  for (const item of elements.values()) {
    const [isExisting, element] = findOrCreateDomElement(item);

    if (isExisting) {
      if (LOCAL_DEVELOPMENT) console.debug('[CrateHelmet] skipped', element);
      continue;
    } else {
      if (LOCAL_DEVELOPMENT) console.debug('[CrateHelmet] appended', element);
      document.head.append(element);
    }
  }

  for (const [key, item] of oldState.elements.entries()) {
    if (newState.elements.has(key)) continue;

    const [isExisting, element] = findOrCreateDomElement(item);

    if (isExisting) {
      if (LOCAL_DEVELOPMENT) console.debug('[CrateHelmet] removed', element);
      element.remove();
    }
  }
}

export const HelmetProviderBrowser: FC<PropsWithChildren> = ({ children }) => {
  const cache = useRef<Set<HeadElement>>(new Set()).current;

  const context: HelmetContextType = {
    addElements(addedElements) {
      const oldState = mergeState([...cache]);
      const newState = mergeState([...cache, ...addedElements]);

      updateDom(oldState, newState);

      for (const element of addedElements) {
        cache.add(element);
      }
    },
    removeElements(removedElements) {
      const excludes = new Set(removedElements);
      const oldState = mergeState([...cache]);
      const newState = mergeState([...cache.difference(excludes)]);

      updateDom(oldState, newState);

      for (const element of removedElements) {
        cache.delete(element);
      }
    },
  };

  return (
    <HelmetContext.Provider value={context}>{children}</HelmetContext.Provider>
  );
};

export const HelmetProviderServer: FC<PropsWithChildren> = ({ children }) => {
  const runtime = useRuntime();

  const context = {
    addElements(elements: HeadElement[]) {
      if (!runtime.isServer) return;
      runtime.response.headElements.push(...elements);
    },
  };

  return (
    <HelmetContext.Provider value={context}>{children}</HelmetContext.Provider>
  );
};

export const HelmetProvider: FC<PropsWithChildren> = ({ children }) => {
  const Provider =
    typeof window !== 'undefined'
      ? HelmetProviderBrowser
      : HelmetProviderServer;

  return <Provider>{children}</Provider>;
};
